import { DateRangeEntry, RequestAnalyticsStat } from "../types";

import {
  AnalyticsDateRange,
  getChartIntervalBucketMinutes,
  getChartIntervalBucketSizeMs,
} from "./dateBuckets";

import { AnalyticsChartInterval, AnalyticsChartIntervalValue } from "@/queries/useRequestAnalytics";
import { CATEGORY_AXIS_LABEL_MIN_POINTS_BEFORE_SPARSE } from "@/utils/dateAxisCategoryLabels";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MINUTE_IN_MS = 60_000;

const padTimePart = (value: number) => String(value).padStart(2, "0");

export const getDateKey = (date: Date) => `${MONTH_LABELS[date.getMonth()]} ${date.getDate()}`;

const getTimeKey = (date: Date) =>
  `${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}`;

const getDateTimeLabel = (date: Date) => `${getDateKey(date)} ${getTimeKey(date)}`;

const getDateTimeWithSecondsLabel = (date: Date) =>
  `${getDateTimeLabel(date)}:${padTimePart(date.getSeconds())}`;

const getDateFromBucketKey = (bucketKey: string) => {
  const timestamp = Number(bucketKey);
  return Number.isFinite(timestamp) ? new Date(timestamp) : new Date(bucketKey);
};

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfLocalWeek = (date: Date) => {
  const day = startOfLocalDay(date);
  day.setDate(day.getDate() - day.getDay());
  return day;
};

const startOfLocalMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const startOfLocalHour = (date: Date) => {
  const bucketStart = new Date(date);
  bucketStart.setMinutes(0, 0, 0);
  return bucketStart;
};

const eachMinuteBucketOfInterval = (
  dateRange: { from: Date; to: Date },
  bucketMinutes: number,
): Date[] => {
  const dates: Date[] = [];
  const current = new Date(dateRange.from);
  current.setSeconds(0, 0);
  current.setMinutes(Math.floor(current.getMinutes() / bucketMinutes) * bucketMinutes);

  while (current <= dateRange.to) {
    dates.push(new Date(current));
    current.setTime(current.getTime() + bucketMinutes * MINUTE_IN_MS);
  }

  return dates;
};

const eachSubMinuteBucketOfInterval = (
  dateRange: { from: Date; to: Date },
  bucketSizeMs: number,
): Date[] => {
  const dates: Date[] = [];
  const current = new Date(dateRange.from);
  current.setMilliseconds(0);
  current.setTime(Math.floor(current.getTime() / bucketSizeMs) * bucketSizeMs);

  while (current <= dateRange.to) {
    dates.push(new Date(current));
    current.setTime(current.getTime() + bucketSizeMs);
  }

  return dates;
};

const eachBucketOfInterval = (
  dateRange: { from: Date; to: Date },
  bucketStart: Date,
  bucketSizeMs: number,
): Date[] => {
  const dates: Date[] = [];
  const current = new Date(bucketStart);

  while (current <= dateRange.to) {
    dates.push(new Date(current));
    current.setTime(current.getTime() + bucketSizeMs);
  }

  return dates;
};

const eachMonthBucketOfInterval = (
  dateRange: { from: Date; to: Date },
  bucketStart: Date,
): Date[] => {
  const dates: Date[] = [];
  const current = new Date(bucketStart);

  while (current <= dateRange.to) {
    dates.push(new Date(current));
    current.setMonth(current.getMonth() + 1);
  }

  return dates;
};

export const buildDateEntriesForRange = (
  dateRange: AnalyticsDateRange,
  chartInterval: AnalyticsChartInterval,
): DateRangeEntry[] => {
  switch (chartInterval.interval) {
    case AnalyticsChartIntervalValue.OneSecond:
    case AnalyticsChartIntervalValue.FifteenSeconds:
      return eachSubMinuteBucketOfInterval(
        dateRange,
        getChartIntervalBucketSizeMs(chartInterval),
      ).map(date => ({
        date,
        key: String(date.getTime()),
        label: getDateTimeWithSecondsLabel(date),
      }));

    case AnalyticsChartIntervalValue.OneMinute:
    case AnalyticsChartIntervalValue.FiveMinutes:
    case AnalyticsChartIntervalValue.FifteenMinutes:
      return eachMinuteBucketOfInterval(
        dateRange,
        getChartIntervalBucketMinutes(chartInterval),
      ).map(date => ({
        date,
        key: String(date.getTime()),
        label: getDateTimeLabel(date),
      }));

    case AnalyticsChartIntervalValue.Hour:
      return eachBucketOfInterval(
        dateRange,
        startOfLocalHour(dateRange.from),
        chartInterval.bucketSizeMs,
      ).map(date => ({
        date,
        key: String(date.getTime()),
        label: getDateTimeLabel(date),
      }));

    case AnalyticsChartIntervalValue.Week:
      return eachBucketOfInterval(
        dateRange,
        startOfLocalWeek(dateRange.from),
        chartInterval.bucketSizeMs,
      ).map(date => ({
        date,
        key: String(date.getTime()),
        label: getDateKey(date),
      }));

    case AnalyticsChartIntervalValue.Month:
      return eachMonthBucketOfInterval(dateRange, startOfLocalMonth(dateRange.from)).map(date => ({
        date,
        key: String(date.getTime()),
        label: `${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`,
      }));

    default:
      return eachBucketOfInterval(
        dateRange,
        startOfLocalDay(dateRange.from),
        chartInterval.bucketSizeMs,
      ).map(date => ({
        date,
        key: getDateKey(date),
        label: getDateKey(date),
      }));
  }
};

export const buildDateEntriesFromStats = (stats: RequestAnalyticsStat[]): DateRangeEntry[] =>
  stats.map(stat => ({
    date: getDateFromBucketKey(stat.date),
    key: stat.date,
    label: stat.dateLabel ?? stat.date,
  }));

export const compactDateRangeAxisLabels = (entries: DateRangeEntry[]): DateRangeEntry[] => {
  if (entries.length === 0) {
    return entries;
  }

  const withTooltip = entries.map(entry => ({
    ...entry,
    tooltipLabel: entry.tooltipLabel ?? entry.label,
  }));

  if (withTooltip.length < CATEGORY_AXIS_LABEL_MIN_POINTS_BEFORE_SPARSE) {
    return withTooltip;
  }

  return withTooltip.map(entry => ({
    ...entry,
    label: entry.tooltipLabel ?? entry.label,
  }));
};
